import type { ApiContext, ApiNotAuthorized, Location, Store, StoreNotFound } from "@hobbyshopify/backend-client";
import { type AsyncResult, ok } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { CustomersRepository } from "../repositories/customers-repository";
import type { PendingCartsRepository } from "../repositories/pending-carts-repository";
import type { ReviewQueueRepository } from "../repositories/review-queue-repository";
import type { StoreRepository, Workspace } from "../repositories/store-repository";
import type { BackendApi } from "./api";

/** Every shop's branches, for the picker. */
export async function loadWorkspaceEntries(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  stores: readonly Store[];
}): AsyncResult<{ store: Store; locations: Location[] }[], ApiNotAuthorized | StoreNotFound> {
  const results = await Promise.all(
    o.stores.map((store) => o.authHandler.callApi((ctx) => o.client.locations.list(ctx, store.id))),
  );
  const entries: { store: Store; locations: Location[] }[] = [];
  for (const [i, r] of results.entries()) {
    if (!r.ok) return r;
    entries.push({ store: o.stores[i], locations: [...r.value.locations] });
  }
  return ok(entries);
}

/** Makes the shop × location current; everything cached for the previous one is fetched again. */
export function chooseWorkspace(o: {
  storeRepository: StoreRepository;
  reviewQueueRepository: ReviewQueueRepository;
  customersRepository: CustomersRepository;
  pendingCartsRepository: PendingCartsRepository;
  workspace: Workspace;
}): void {
  o.storeRepository.choose(o.workspace);
  o.reviewQueueRepository.markStale();
  o.customersRepository.markStale();
  o.pendingCartsRepository.markStale();
}

export function switchWorkspace(o: { storeRepository: StoreRepository }): void {
  o.storeRepository.forgetWorkspace();
}
